import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer } from "lucide-react";
import { LUCIDA } from "@/components/Letterhead";

const dfmt = (s) => { try { return new Date(s).toLocaleDateString("de-DE"); } catch { return s; } };
const nf = (n, d = 2) => Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: d, maximumFractionDigits: d });

export default function CN23() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  useEffect(() => { api.get(`/cn23/${id}`).then((r) => setC(r.data)).catch(() => {}); }, [id]);
  if (!c) return <div className="text-muted-foreground p-8">Lädt…</div>;

  const Cell = ({ children, className = "" }) => <td className={`border border-neutral-400 px-2 py-1 align-top ${className}`}>{children}</td>;
  const HCell = ({ children, className = "" }) => <th className={`border border-neutral-400 px-2 py-1 text-left font-semibold ${className}`}>{children}</th>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 print:hidden">
        <button onClick={() => nav(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="cn23-back"><ArrowLeft className="h-4 w-4" /> Zurück</button>
        <Button onClick={() => window.print()} data-testid="cn23-print"><Printer className="h-4 w-4 mr-1.5" /> Drucken / PDF</Button>
      </div>

      <div className="mx-auto a4-page bg-white text-black text-sm" style={LUCIDA} data-testid="cn23-doc">
        <div className="flex justify-between items-start border-b-2 border-black pb-2">
          <div>
            <div className="text-xl font-bold">CN 23</div>
            <div className="text-xs">CUSTOMS DECLARATION · Déclaration en douane</div>
          </div>
          <div className="text-right text-xs">
            <div className="font-semibold">{c.cn23_number}</div>
            <div>{dfmt(c.issued_at)}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-3">
          <div>
            <div className="text-xs font-semibold uppercase text-neutral-500">From / Absender</div>
            <div className="font-semibold">{c.sender?.name}</div>
            <div className="whitespace-pre-line text-xs">{c.sender?.office_address}</div>
            {c.sender?.email && <div className="text-xs">{c.sender.email}</div>}
            <div className="text-xs mt-1">EORI: {c.sender?.eori || "—"}</div>
            <div className="text-xs">VAT-ID: {c.sender?.vat_id || "—"}</div>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase text-neutral-500">To / Empfänger</div>
            <div className="font-semibold" data-testid="cn23-recipient">{c.recipient?.name}</div>
            <div className="whitespace-pre-line text-xs">{c.recipient?.address}</div>
            <div className="text-xs mt-1">Country: {c.recipient?.country || "—"}</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
          <div><span className="text-neutral-500">Order No.:</span> {c.shipment?.order_no}</div>
          <div><span className="text-neutral-500">Invoice (14):</span> {c.shipment?.invoice_no || "—"}</div>
          <div><span className="text-neutral-500">Ref.:</span> {c.shipment?.po_number || "—"}</div>
        </div>

        <table className="w-full border-collapse mt-3 text-xs">
          <thead className="bg-neutral-100">
            <tr>
              <HCell>Description of Contents (1)</HCell>
              <HCell>HS Tariff No. (7)</HCell>
              <HCell className="text-right">Qty (2)</HCell>
              <HCell className="text-right">Net Wt. kg (3)</HCell>
              <HCell className="text-right">Value EUR (5)</HCell>
              <HCell>Origin (8)</HCell>
            </tr>
          </thead>
          <tbody>
            {c.lines.map((l, i) => (
              <tr key={i}>
                <Cell>{l.description}</Cell>
                <Cell>{l.hs_code || "—"}</Cell>
                <Cell className="text-right">{l.quantity}</Cell>
                <Cell className="text-right">{nf(l.net_weight_kg, 3)}</Cell>
                <Cell className="text-right">{nf(l.value_eur)}</Cell>
                <Cell>{l.country_of_origin || "—"}</Cell>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="font-semibold bg-neutral-50">
              <Cell className="text-right" >Totals (4/6)</Cell>
              <Cell></Cell><Cell></Cell>
              <Cell className="text-right">{nf(c.totals?.gross_weight_kg, 3)}</Cell>
              <Cell className="text-right">{nf(c.totals?.total_value_eur)}</Cell>
              <Cell></Cell>
            </tr>
          </tfoot>
        </table>

        <div className="mt-3 text-xs">
          <div className="font-semibold">Category of item (10) / Art der Sendung</div>
          <div className="flex flex-wrap gap-4 mt-1">
            {["Sale of goods", "Gift", "Commercial sample", "Documents", "Returned goods", "Other"].map((cat) => (
              <label key={cat} className="flex items-center gap-1"><span className={`inline-block h-3 w-3 border border-black ${c.category === cat ? "bg-black" : ""}`} /> {cat}</label>
            ))}
          </div>
        </div>

        <div className="mt-4 text-[10px] text-neutral-600 border-t border-neutral-300 pt-2">
          I certify that the particulars given in this customs declaration are correct and that this item does not contain any dangerous article or articles prohibited by legislation or by postal or customs regulations.
        </div>
        <div className="mt-8 flex justify-between text-xs">
          <div>Date: {dfmt(c.issued_at)}</div>
          <div>Sender's Signature (15): ______________________</div>
        </div>
      </div>
    </div>
  );
}
